import { jsx, jsxs } from 'react/jsx-runtime';
import { Breadcrumbs } from '../page-layout/Breadcrumbs.js';
import { Button } from '../page-layout/Button.js';
import { DataTable } from '../page-layout/DataTable.js';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import { buildFilterWhere, countActiveFilters } from '../../utils/buildFilterWhere.js';
import 'date-fns-tz';
import 'clsx';
import 'tailwind-merge';
import { AddItemModal } from './AddItemModal.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Plus, Database, Filter } from 'lucide-react';
import './CrudDetailView.js';
import './CrudFormView.js';
import { CrudListHeader } from './CrudListHeader.js';
import '../../contexts/AppContext.js';
import { useOrganization } from '../../contexts/OrganizationContext.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useApiClient } from '../../lib/api-client.js';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { useSavedListViews } from '../../hooks/useSavedListViews.js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListFilters } from './ListFilters.js';
import { SaveListDialog } from './SaveListDialog.js';
import { SavedListSelector } from './SavedListSelector.js';
import { useCrudActions } from './useCrudActions.js';
import '@radix-ui/react-dialog';
import '../../lib/toast.js';
import '../ui/badge.js';
import 'react-day-picker';
import '../ui/button.js';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import 'cmdk';
import '@radix-ui/react-popover';
import '@radix-ui/react-label';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import { EmptyTableState } from '../ui/empty-state.js';
import { ErrorState } from '../ui/error-state.js';
import '@radix-ui/react-progress';
import '../ui/radio-group.js';
import '@radix-ui/react-select';
import '../ui/time-input.js';
import '@radix-ui/react-separator';
import '../ui/sidebar.js';
import 'react-toastify';
import '@radix-ui/react-switch';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';

/**
 * Generic CRUD list view component with built-in support for standard CRUD actions
 * Standard actions (configured in config.listView.actions):
 * - Header: add (modal), create (navigate)
 * - Row: view (navigate), edit (navigate), delete (API call with confirm), remove (custom with confirm)
 */
function CrudListView({ config, onItemClick, className = '', defaultWhere = {}, breadcrumbs, customActions = [], headerActions = [], headerRight, contentOverride, getValueFromItem = (item) => item.id, getLabelFromItem = (item) => item.name, baseUrl, listId, }) {
    const { organization } = useOrganization();
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Pagination lives in the URL (`?page=N`) so the browser Back button restores
    // the page the user was on after they open an item and return (SUP-18). Page 1
    // is represented by the absence of the param to keep clean URLs clean.
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
    // Live refs to the latest search params + setter. react-router gives the setter
    // a fresh identity whenever the URL changes; reading it through a ref lets the
    // filter-reset effect below depend on `filterStateKey` alone, so writing the
    // page param can't retrigger that effect and immediately wipe the page.
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;
    const setSearchParamsRef = useRef(setSearchParams);
    setSearchParamsRef.current = setSearchParams;
    // Write the page into the URL (`?page=N`, omitted for page 1), preserving any
    // other params. Stable identity (reads via refs) so memoised children don't
    // re-render on every URL change.
    const setPageParam = useCallback((newPage) => {
        const next = new URLSearchParams(searchParamsRef.current);
        if (newPage <= 1) {
            next.delete('page');
        }
        else {
            next.set('page', String(newPage));
        }
        setSearchParamsRef.current(next, { replace: true });
    }, []);
    // State for list controls
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState(config.listView.defaultSort?.field);
    const [sortOrder, setSortOrder] = useState(config.listView.defaultSort?.direction || 'asc');
    // Filter state
    const [filterState, setFilterState] = useState({});
    const [filtersOpen, setFiltersOpen] = useState(false);
    // Saved list views
    const hasFilters = (config.listView.filters?.length ?? 0) > 0;
    // Search placement: lists with a filter toolbar keep the full-width search in
    // the DataTable toolbar; filter-less lists get a compact search in the header.
    const searchable = config.listView.searchable ?? true;
    const showHeaderSearch = searchable && !hasFilters;
    const dataTableSearchable = searchable && !showHeaderSearch;
    const { views, saveView, updateView, deleteView } = useSavedListViews(config.entityName);
    const [selectedListId, setSelectedListId] = useState(listId || null);
    const [listApplied, setListApplied] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [editingView, setEditingView] = useState(null);
    const [deleteConfirmView, setDeleteConfirmView] = useState(null);
    // State for confirmation dialog
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        loading: false,
    });
    // State for add item modal
    const [addModalOpen, setAddModalOpen] = useState(false);
    // Debounce search to prevent excessive API calls
    const debouncedSearch = useDebouncedValue(search, 300);
    // Reset to page 1 when filters change. Guard the initial render so we don't
    // clobber a page restored from the URL on mount — only an actual post-mount
    // filter change should drop the user back to page 1. Depends on
    // `filterStateKey` ALONE (page writes go through the ref-backed setter) so a
    // page change can't retrigger this effect and wipe the page the user just set.
    const filterStateKey = JSON.stringify(filterState);
    const isFirstFilterRun = useRef(true);
    useEffect(() => {
        if (isFirstFilterRun.current) {
            isFirstFilterRun.current = false;
            return;
        }
        setPageParam(1);
    }, [filterStateKey, setPageParam]);
    // Build filter where clause
    const filterWhere = useMemo(() => {
        if (!config.listView.filters || !config.listView.filters.length)
            return undefined;
        return buildFilterWhere(filterState, config.listView.filters);
    }, [filterState, config.listView.filters]);
    // Merge defaultWhere and filterWhere
    const combinedWhere = useMemo(() => {
        const hasDefault = Object.keys(defaultWhere).length > 0;
        if (!hasDefault && !filterWhere)
            return undefined;
        if (!hasDefault)
            return filterWhere;
        if (!filterWhere)
            return defaultWhere;
        return { AND: [defaultWhere, filterWhere] };
    }, [defaultWhere, filterWhere]);
    // Active filter count for badge
    const activeFilterCount = countActiveFilters(filterState);
    // When a saved list is selected, apply its filters/sort/columns and update URL
    const handleSelectList = useCallback((view) => {
        if (!view) {
            setSelectedListId(null);
            setFilterState({});
            setSortField(config.listView.defaultSort?.field);
            setSortOrder(config.listView.defaultSort?.direction || 'asc');
            setFiltersOpen(false);
            // Navigate back to base list URL
            if (baseUrl && listId) {
                navigate(baseUrl, { replace: true });
            }
            return;
        }
        setSelectedListId(view.id);
        setFilterState(view.filters || {});
        if (view.sortField) {
            setSortField(view.sortField);
            setSortOrder(view.sortOrder || 'asc');
        }
        // Update URL to include the list ID
        if (baseUrl) {
            navigate(`${baseUrl}/list/${view.id}`, { replace: true });
        }
    }, [config.listView.defaultSort, baseUrl, listId, navigate]);
    // Auto-apply saved list from URL param once views are loaded
    useEffect(() => {
        if (listId && views.length > 0 && !listApplied) {
            const view = views.find(v => v.id === listId);
            if (view) {
                setSelectedListId(view.id);
                setFilterState(view.filters || {});
                if (view.sortField) {
                    setSortField(view.sortField);
                    setSortOrder(view.sortOrder || 'asc');
                }
                setListApplied(true);
            }
        }
    }, [listId, views, listApplied]);
    // Get list fields from listView.columns or listView.fields configuration
    // When a saved list is selected, use its column config
    const columns = useMemo(() => {
        const selectedView = views.find(v => v.id === selectedListId);
        const columnKeys = selectedView?.columns;
        // If saved list has custom columns, use those
        if (columnKeys && columnKeys.length > 0) {
            return columnKeys
                .map(key => {
                const field = config.fields.find(f => f.key === key);
                if (!field)
                    return undefined;
                return {
                    key: field.key,
                    label: field.label,
                    type: field.type,
                    sortable: config.listView.sortableFields.includes(field.key),
                    format: field.format,
                    disableRowClick: field.disableRowClick,
                    align: field.align,
                };
            })
                .filter(col => col !== undefined);
        }
        // Use columns config if provided (supports hideOnMobile)
        if (config.listView.columns) {
            return config.listView.columns
                .map(columnConfig => {
                const field = config.fields.find(f => f.key === columnConfig.field);
                if (!field)
                    return undefined;
                return {
                    key: field.key,
                    label: field.label,
                    type: field.type,
                    sortable: config.listView.sortableFields.includes(field.key),
                    format: field.format,
                    disableRowClick: field.disableRowClick,
                    align: field.align,
                    hideOnMobile: columnConfig.hideOnMobile,
                };
            })
                .filter(col => col !== undefined);
        }
        // Fall back to simple fields config (no mobile hiding)
        return (config.listView.fields || [])
            .map(fieldKey => config.fields.find(field => field.key === fieldKey))
            .filter(field => field !== undefined)
            .map(field => ({
            key: field.key,
            label: field.label,
            type: field.type,
            sortable: config.listView.sortableFields.includes(field.key),
            format: field.format,
            disableRowClick: field.disableRowClick,
            align: field.align,
        }));
    }, [
        config.fields,
        config.listView.fields,
        config.listView.columns,
        config.listView.sortableFields,
        selectedListId,
        views,
    ]);
    // Build query parameters
    const queryParams = useMemo(() => ({
        page,
        limit: config.listView.itemsPerPage || 50,
        search: debouncedSearch || undefined,
        orderBy: sortField ? { [sortField]: sortOrder } : undefined,
        where: combinedWhere,
    }), [page, config.listView.itemsPerPage, debouncedSearch, sortField, sortOrder, combinedWhere]);
    // Detect if endpoint is a nested resource path (e.g., /tours/:id/attractions)
    // Top-level paths like /attractions have 1 segment; nested have 3+
    const isNestedEndpoint = config.endpoints.list.split('/').filter(Boolean).length > 1;
    // Fetch data — use POST /list for top-level entities, GET with params for nested
    const { data: response, isLoading, error, refetch, } = useQuery({
        queryKey: [config.entityName, 'list', organization?.id, queryParams],
        queryFn: async () => {
            const result = isNestedEndpoint
                ? await apiClient.get(config.endpoints.list, {
                    params: queryParams,
                })
                : await apiClient.post(`${config.endpoints.list}/list`, queryParams);
            // Validate response format
            if (typeof result !== 'object' || result === null) {
                throw new Error('Invalid response format: expected JSON object');
            }
            if (!('data' in result) || !('pagination' in result)) {
                throw new Error('Invalid response structure: missing required fields (data, pagination)');
            }
            return result;
        },
        enabled: Boolean(queryParams) && !contentOverride,
    });
    const items = response?.data || [];
    const pagination = response?.pagination;
    const actionConfig = config.listView.actions || {};
    /**
     * Fetch items for add modal
     */
    const getItemsForAdd = useCallback(async (params) => {
        const addConfig = actionConfig.add;
        const endpoint = addConfig?.endpoint || config.endpoints.list;
        const where = addConfig?.where || {};
        const response = await apiClient.get(endpoint, {
            params: {
                page: params.page,
                limit: params.limit,
                orderBy: { name: 'asc' },
                ...(params.search && { search: params.search }),
                ...(Object.keys(where).length > 0 && { where }),
            },
        });
        return response;
    }, [actionConfig.add, apiClient, config.endpoints.list, config.listView.actions]);
    /**
     * Handle add item submission
     */
    const handleAddItem = async (item) => {
        const addConfig = actionConfig.add;
        if (addConfig?.onAdd) {
            await addConfig.onAdd(item);
        }
        // Close modal
        setAddModalOpen(false);
        // Invalidate and refetch both the list and the modal data
        queryClient.invalidateQueries({ queryKey: [config.entityName, 'list', organization?.id] });
        refetch();
    };
    /**
     * Handle delete item
     */
    const handleDelete = async (item) => {
        const deleteConfig = actionConfig.delete;
        try {
            if (deleteConfig?.onDelete) {
                await deleteConfig.onDelete(item);
            }
            else {
                // Default delete behavior
                const itemId = getValueFromItem(item);
                await apiClient.delete(config.endpoints.delete(itemId));
            }
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: [config.entityName, 'list', organization?.id] });
            refetch();
        }
        catch (error) {
            console.error(`Failed to delete ${config.entityName}:`, error);
            throw error;
        }
    };
    /**
     * Handle remove item
     */
    const handleRemove = async (item) => {
        const removeConfig = actionConfig.remove;
        if (removeConfig?.onRemove) {
            await removeConfig.onRemove(item);
            refetch();
        }
    };
    /**
     * Handle confirmation action callback for actions that require confirmation
     */
    const handleConfirmActionSetup = useCallback((action, item) => {
        setConfirmDialog({
            open: true,
            action,
            item,
            loading: false,
        });
    }, []);
    // Build row actions from config using the hook
    const dataTableActions = useCrudActions({
        config,
        customActions,
        baseUrl,
        getValueFromItem,
        navigate,
        onDelete: handleDelete,
        onRemove: handleRemove,
        onConfirmAction: handleConfirmActionSetup,
    });
    /**
     * Handle confirmation dialog confirm action
     */
    const handleConfirmAction = useCallback(async () => {
        if (!confirmDialog.action || !confirmDialog.item)
            return;
        setConfirmDialog(prev => ({ ...prev, loading: true }));
        try {
            await confirmDialog.action.onClick(confirmDialog.item);
            // Refetch data after successful action
            refetch();
        }
        catch (error) {
            console.error('Error executing action:', error);
        }
        finally {
            setConfirmDialog({
                open: false,
                loading: false,
            });
        }
    }, [confirmDialog.action, confirmDialog.item, refetch]);
    /**
     * Handle sorting when column header is clicked
     */
    const handleSort = useCallback((field, direction) => {
        setSortField(field);
        setSortOrder(direction);
    }, []);
    /**
     * Handle search input changes
     */
    const handleSearch = useCallback((query) => {
        setSearch(query);
    }, []);
    /**
     * Handle page changes by syncing the page into the URL (`?page=N`). `replace`
     * (in {@link setPageParam}) keeps paging from stacking history entries — Back
     * from a detail page returns straight to the list at the page the user was on.
     */
    const handlePageChange = useCallback((newPage) => {
        setPageParam(newPage);
    }, [setPageParam]);
    /**
     * Handle row click with support for Cmd/Ctrl+click to open in new window
     */
    const handleRowClick = useCallback((item, event) => {
        // If custom onItemClick is provided, use it
        if (onItemClick) {
            onItemClick(item);
            return;
        }
        // Default behavior: navigate to detail view
        const viewConfig = actionConfig.view;
        if (!viewConfig?.enabled)
            return;
        const itemId = getValueFromItem(item);
        let url;
        if (viewConfig.getUrl) {
            url = viewConfig.getUrl(item);
        }
        else {
            url = baseUrl ? `${baseUrl}/${itemId}` : config.endpoints.single(itemId);
        }
        // Cmd/Ctrl+click opens in new window
        if (event && (event.metaKey || event.ctrlKey)) {
            window.open(url, '_blank');
        }
        else {
            navigate(url);
        }
    }, [onItemClick, actionConfig, getValueFromItem, baseUrl, config.endpoints, navigate]);
    /**
     * Build empty state component with smart defaults
     */
    const emptyStateComponent = useMemo(() => {
        const emptyConfig = config.listView.emptyState;
        // Determine if we should show an action button
        const hasAddAction = actionConfig.add?.enabled;
        const hasCreateAction = actionConfig.create?.enabled;
        let action;
        // Prioritize custom empty state action if provided
        if (emptyConfig?.action) {
            action = emptyConfig.action;
        }
        else if (hasAddAction) {
            action = {
                label: actionConfig.add?.label || `Add ${config.entityName}`,
                onClick: () => setAddModalOpen(true),
                icon: Plus,
            };
        }
        else if (hasCreateAction && actionConfig.create) {
            action = {
                label: actionConfig.create.label || `Create ${config.entityName}`,
                onClick: () => {
                    if (actionConfig.create?.onClick) {
                        actionConfig.create.onClick();
                    }
                    else {
                        const url = actionConfig.create?.url ||
                            (baseUrl ? `${baseUrl}/new` : `${config.endpoints.create}/new`);
                        navigate(url);
                    }
                },
                icon: Plus,
            };
        }
        return (jsx(EmptyTableState, { icon: emptyConfig?.icon || jsx(Database, { className: "h-12 w-12" }), title: emptyConfig?.title || `No ${config.entityNamePlural.toLowerCase()}`, description: emptyConfig?.description ||
                `Get started by creating your first ${config.entityName.toLowerCase()}`, action: action }));
    }, [config, actionConfig, baseUrl, navigate]);
    // Toolbar elements for DataTable
    const toolbarLeft = hasFilters ? (jsx(SavedListSelector, { entityNamePlural: config.entityNamePlural, views: views, selectedListId: selectedListId, onSelectList: handleSelectList, onSaveNew: () => {
            setEditingView(null);
            setSaveDialogOpen(true);
        }, onEdit: view => {
            setEditingView(view);
            setSaveDialogOpen(true);
        }, onDelete: view => setDeleteConfirmView(view) })) : undefined;
    const toolbarRight = hasFilters ? (jsxs(Button, { variant: activeFilterCount > 0 ? 'default' : 'outline', className: "h-9 gap-2 shrink-0", onClick: () => setFiltersOpen(true), children: [jsx(Filter, { className: "h-4 w-4" }), jsx("span", { className: "hidden sm:inline", children: "Filter" }), activeFilterCount > 0 && (jsxs("span", { className: "hidden sm:inline text-xs opacity-80", children: ["(", activeFilterCount, ")"] }))] })) : undefined;
    return (jsxs("div", { className: className, children: [jsxs("div", { className: "space-y-4", children: [breadcrumbs && jsx(Breadcrumbs, { items: breadcrumbs }), jsx(CrudListHeader, { config: config, baseUrl: baseUrl, navigate: navigate, onAddClick: () => setAddModalOpen(true), customActions: headerActions, headerRight: headerRight, search: showHeaderSearch
                            ? {
                                value: search,
                                onChange: handleSearch,
                                placeholder: `Search ${config.entityNamePlural.toLowerCase()}...`,
                            }
                            : undefined }), contentOverride ? (contentOverride) : error ? (jsx(ErrorState, { title: `Error Loading ${config.entityNamePlural}`, error: error, action: {
                            label: 'Retry',
                            onClick: () => refetch(),
                        } })) : columns.length === 0 ? (jsx(ErrorState, { title: 'Configuration Error', description: `No list fields configured for ${config.entityName}. Please check config.listView.columns or config.listView.fields configuration.` })) : (jsx(DataTable, { columns: columns, data: items, loading: isLoading, pagination: pagination, searchable: dataTableSearchable, searchPlaceholder: `Search ${config.entityNamePlural.toLowerCase()}...`, emptyMessage: `No ${config.entityNamePlural.toLowerCase()} found`, emptyState: emptyStateComponent, onRowClick: handleRowClick, onSearch: handleSearch, onSort: handleSort, onPageChange: handlePageChange, currentSort: sortField ? { field: sortField, direction: sortOrder } : undefined, actions: dataTableActions, skeletonRows: config.listView.itemsPerPage || 10, toolbarLeft: toolbarLeft, toolbarRight: toolbarRight }))] }), jsx(ConfirmDialog, { open: confirmDialog.open, onOpenChange: open => setConfirmDialog(prev => ({ ...prev, open })), title: confirmDialog.action?.confirm?.title || 'Confirm Action', description: confirmDialog.action?.confirm?.description, confirmText: confirmDialog.action?.confirm?.confirmText || 'Confirm', cancelText: confirmDialog.action?.confirm?.cancelText || 'Cancel', variant: confirmDialog.action?.variant === 'destructive' ? 'destructive' : 'default', loading: confirmDialog.loading, onConfirm: handleConfirmAction }), actionConfig.add?.enabled && (jsx(AddItemModal, { open: addModalOpen, onOpenChange: setAddModalOpen, entityName: config.entityName, entityNamePlural: config.entityNamePlural, onAdd: handleAddItem, getItems: getItemsForAdd, getValueFromItem: getValueFromItem, getLabelFromItem: getLabelFromItem, modalConfig: actionConfig.add.modal })), hasFilters && (jsx(ListFilters, { filters: config.listView.filters, filterState: filterState, onFilterChange: setFilterState, onClear: () => setFilterState({}), open: filtersOpen, onOpenChange: setFiltersOpen })), hasFilters && (jsx(SaveListDialog, { open: saveDialogOpen, onOpenChange: setSaveDialogOpen, config: config, currentFilters: filterState, currentSortField: sortField, currentSortOrder: sortOrder, editingView: editingView, onSave: saveView, onUpdate: updateView })), jsx(ConfirmDialog, { open: Boolean(deleteConfirmView), onOpenChange: open => {
                    if (!open)
                        setDeleteConfirmView(null);
                }, title: "Delete Saved View", description: `Are you sure you want to delete "${deleteConfirmView?.name}"? This action cannot be undone.`, confirmText: "Delete", cancelText: "Cancel", variant: "destructive", loading: false, onConfirm: async () => {
                    if (deleteConfirmView) {
                        await deleteView(deleteConfirmView.id, deleteConfirmView.scope);
                        if (selectedListId === deleteConfirmView.id) {
                            handleSelectList(null);
                        }
                        setDeleteConfirmView(null);
                    }
                } })] }));
}

export { CrudListView };
//# sourceMappingURL=CrudListView.js.map
